import { flexRender, type Header } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react';

interface Props<T> {
    header: Header<T, unknown>;
    tableSortable: boolean;
    width?: number;
    /** Pins the cell to the top of the table's scroll region; the pseudo-element rule travels with it. */
    sticky?: boolean;
}

const STICKY_CLASSES =
    'sticky top-0 z-10 bg-mp-background after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-mp-border';

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

function SortIcon({ direction }: { direction: false | 'asc' | 'desc' }) {
    const Icon = direction === 'asc' ? ChevronUpIcon : direction === 'desc' ? ChevronDownIcon : ChevronsUpDownIcon;
    return <Icon aria-hidden="true" className="size-4 text-mp-muted" />;
}

export function TableHeaderCell<T>({ header, tableSortable, width, sticky = false }: Props<T>) {
    const canSort = tableSortable && header.column.getCanSort();
    const direction = header.column.getIsSorted();
    const content = flexRender(header.column.columnDef.header, header.getContext());

    return (
        <th
            style={width !== undefined ? { width } : undefined}
            aria-sort={canSort ? (direction ? ARIA_SORT[direction] : 'none') : undefined}
            className={`${tableSortable && !canSort ? 'cursor-not-allowed' : ''} ${
                sticky ? STICKY_CLASSES : ''
            } px-4 py-3 text-left font-medium text-mp-foreground`}
        >
            {canSort ? (
                <button
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    className="-mx-2 -my-1 flex items-center gap-2 rounded px-2 py-1 font-medium select-none hover:bg-mp-border focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mp-primary"
                >
                    {content}
                    <SortIcon direction={direction} />
                </button>
            ) : (
                <div className="flex items-center gap-2">{content}</div>
            )}
        </th>
    );
}
